import React, { useEffect, useMemo } from 'react';
import {
	TableCell,
	TableRow,
	Typography,
	Button,
	Stack,
	Checkbox,
	Avatar,
	Chip,
	Tooltip,
	IconButton,
	Box,
	useTheme,
} from '@mui/material';
import { formatDate, formatDateString, getPipelineIcon } from '@/utils/constants';
import { BlinkingCircle } from '../icons/BlinkingCircle';
import { useDispatch, useSelector } from 'react-redux';
import { Delete } from '@mui/icons-material';
import { useRemoveDealPolicyMutation } from '@/features/deals/dealsApi';
import { showToast } from '@/features/toast/toastSlice';

export const PolicyRow = ({
	policy,
	handleCheckboxChange,
	hideCheckbox,
	showStatus = false,
	showDisabledRow = false,
	selected,
	dealId,
	clientId,
	isDebtor = false,
	canRemoveTransaction = false,
}) => {
	const theme = useTheme();
	const dispatch = useDispatch();

	const showDisabled = showDisabledRow
		? policy?.associated ||
			policy?.linkedToCurrentDeal ||
			(policy?.policyTransactions === null && (policy?.policyTransactions ?? []).length > 0)
		: false;

	const latestInvoice = policy?.policyTransactions?.length
		? policy.policyTransactions.find((t) => t.isCurrent || t.IsLatest || t.IsLatestInPeriod) ||
			policy.policyTransactions[0]
		: {};

	const policyStatus = useMemo(() => {
		return (policy?.isCurrent ?? policy?.IsLatestInPeriod === true) ? '#5CE65C' : '#FF604B';
	}, [policy]);

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const [removeDealPolicy, { isLoading: isRemoving }] = useRemoveDealPolicyMutation();

	const handleRemoveDealPolicy = () => {
		if (policy) {
			removeDealPolicy({
				dealId: dealId,
				clientId: clientId,
				policyId: policy?.PolicyId,
				policyTransactionId: policy?.Id,
			})
				.unwrap()
				.catch((error) => {
					dispatch(
						showToast({
							message: `Error unlinking Transaction ${policy?.PolicyNumber} - ${policy?.Description ?? policy?.BriefDescription}`,
							action: {
								label: 'Unlink Transaction',
							},
							autohide: false,
							type: 'error',
							duration: 5000,
						}),
					);
					console.error('Error unlinking policy:', error);
				});
		} else {
			console.error('missing policy: ', policy);
		}
	};

	return (
		<Tooltip
			title={
				showDisabled
					? policy?.linkedToCurrentDeal
						? 'This policy transaction has already been associated with this deal'
						: 'This policy transaction has already been associated with an existing deal'
					: ''
			}
			placement='top'
		>
			<TableRow
				key={`table-row-${policy.Id}`}
				sx={{
					...(showDisabled && {
						backgroundColor: theme.palette.action.disabledBackground,
					}),
				}}
			>
				{!hideCheckbox && (
					<TableCell size='small' padding='checkbox' sx={{ alignItems: 'center' }}>
						<Checkbox
							checked={selected || showDisabled}
							onChange={(event) => handleCheckboxChange(event, policy.Id)}
							color='primary'
							{...(showDisabled && { disabled: true })}
						/>
					</TableCell>
				)}
				<TableCell sx={{ whiteSpace: 'wrap' }}>
					<Stack
						direction='column'
						sx={{ justifyContent: 'space-between', display: 'flex' }}
						spacing={1}
						width='100%'
					>
						{policy &&
						!policy?.IsPosted &&
						!policy?.IsImported &&
						!policy?.IsTakeUp &&
						!policy?.IsLapsed ? (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									position: 'relative',
									height: '100%',
								}}
							>
								<Box
									sx={{
										backgroundColor: 'orange',
										fontWeight: 'bold',
										color: 'white',
										padding: '2px 4px',
										fontSize: '0.7em',
										whiteSpace: 'nowrap',
									}}
								>
									{'DRAFT'}
								</Box>
							</Box>
						) : (
							<>
								<Typography>
									{policy?.IsTakeUp && !policy?.IsPosted
										? 'Take up'
										: policy?.IsLapsed
											? 'Lapsed'
											: policy?.IsImported
												? policy?.ImportedInvoiceNumber
												: latestInvoice?.IsImported
													? latestInvoice?.ImportedInvoiceNumber
													: (latestInvoice?.InvoiceNumber ??
														policy?.InvoiceNumber ??
														'-')}
								</Typography>
							</>
						)}
						<Typography>
							{policy?.Description ?? policy?.BriefDescription ?? '-'}
						</Typography>
					</Stack>
				</TableCell>
				<TableCell width={'10%'}>
					<Stack
						direction='column'
						sx={{ justifyContent: 'space-between' }}
						spacing={1}
						display={'flex'}
					>
						<Typography>{policy?.PolicyNumber}</Typography>
						<Typography>{policy?.ClassOfRiskName ?? policy?.ClassOfRisk}</Typography>
					</Stack>
				</TableCell>
				<TableCell>
					<Stack direction='row' alignItems={'center'} spacing={1}>
						{getPipelineIcon(
							policy?.TransactionTypeName ??
								policy?.TransactionType ??
								latestInvoice?.TransactionType,
							'1.5em',
							theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
						)}
						<Typography>
							{`${policy?.TransactionTypeName ?? latestInvoice?.TransactionType}${
								policy?.IsAmend || latestInvoice?.IsAmendTransaction ? ' Amend' : ''
							}`}
						</Typography>
					</Stack>
				</TableCell>
				<TableCell width={'20%'}>
					<Stack direction='column' spacing={1} width='100%' display={'flex'}>
						<Typography variant='subtitle2'>
							From: {formatDateString(policy?.FromDate)}
						</Typography>
						<Typography variant='subtitle2'>
							To: {formatDateString(policy?.ToDate)}
						</Typography>
						{policy?.TransactionTypeName === 'Endorsement' && (
							<Typography variant='subtitle2'>
								Effective: {formatDateString(policy?.EffectiveDate)}
							</Typography>
						)}
					</Stack>
				</TableCell>
				<TableCell>{policy.InsurerName}</TableCell>
				{showStatus && (
					<TableCell>
						<Stack direction='row' spacing={1} alignItems={'center'}>
							<BlinkingCircle fontSize='1px' color={policyStatus} />
							<Typography>
								{policy?.isCurrent || policy?.IsLatestInPeriod ? 'Yes' : 'No'}
							</Typography>
						</Stack>
					</TableCell>
				)}
				{canRemoveTransaction && (
					<TableCell align='right'>
						<IconButton
							aria-label='delete'
							size='small'
							disabled={isRemoving}
							onClick={handleRemoveDealPolicy}
							sx={{
								color: theme.palette.primary.main,
								position: 'relative',
								fontSize: '1.25rem !important',
							}}
						>
							<Delete fontSize='small' sx={{ fontSize: '1.25rem !important' }} />
						</IconButton>
					</TableCell>
				)}
			</TableRow>
		</Tooltip>
	);
};
